import * as yup from 'yup';
import { ReminderStatusEnum, SelectionStatusEnum } from 'redux-store/api/selections';
import { TSelectionFormOption } from '../utils/types';

const urlRegexp = /[a-zA-Z0-9@:%._+~#?&//=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%._+~#?&//=]*)/;
const quantityRegexp = /^[+]?[1-9]\d*$/;
const amountRegexp = /^\d+(\.\d+)?$/;
const selectionTitleSchema = yup
  .string()
  .max(250, 'Must be less then 250 characters')
  .when('options', (options: TSelectionFormOption[], field: any) => {
    if (options.length > 1) {
      return field.trim().required('Option name is a required field');
    }

    return field;
  });

const selectionOptionSchema = yup.object({
  title: yup
    .string()
    .trim()
    .max(250, 'Must be less then 250 characters')
    .required('Selection name is a required field'),

  quantity: yup
    .string()
    .trim()
    .required('Quantity is a required field')
    .matches(quantityRegexp, 'Quantity must be an integer'),

  amount: yup
    .string()
    .trim()
    .required('Amount is a required field')
    .typeError('Amount is a required field')
    .matches(amountRegexp, 'Amount must be a number'),

  url: yup.lazy((value) =>
    !value ? yup.string().nullable() : yup.string().matches(urlRegexp, 'Must be valid url')
  ),

  description: yup.string().nullable().max(3000, 'Must be less then 3000 characters'),
});

export const librarySelectionSchema = yup.object({
  title: selectionTitleSchema,
  options: yup.array().of(selectionOptionSchema),
  costCodeId: yup
    .number()
    .required('Cost Code is a required field')
    .typeError('Cost Code is a required field')
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    }),
  roomId: yup
    .number()
    .required('Location is a required field')
    .typeError('Location is a required field')
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    }),
});

export const selectionGeneralSchema = yup.object({
  title: selectionTitleSchema,

  costCodeId: yup
    .number()
    .required('Cost Code is a required field')
    .typeError('Cost Code is a required field'),

  roomId: yup
    .number()
    .required('Location is a required field')
    .typeError('Location is a required field'),

  options: yup.array().of(selectionOptionSchema),
});

const selectionReminderSchema = yup.object({
  status: yup.string().nullable(),

  endDate: yup
    .string()
    .nullable()
    .when('reminderStatus', (reminderStatus: ReminderStatusEnum, field: any) =>
      reminderStatus === ReminderStatusEnum.CUSTOM
        ? field.required('Deadline Date is a required field').default(() => new Date().toString())
        : field
    ),

  projectPhaseId: yup.mixed().when(['reminderStatus', 'status'], {
    is: (reminderStatus: ReminderStatusEnum, status: SelectionStatusEnum) =>
      reminderStatus === ReminderStatusEnum.SCHEDULE_PHASE &&
      status === SelectionStatusEnum.PENDING,
    then: yup
      .number()
      .nullable()
      .required('Schedule Phase is a required field')
      .typeError('Schedule Phase must be a number'),
    otherwise: yup.mixed().test('skip-validation', 'Skipping validation', () => true),
  }),

  projectPhaseItemId: yup.mixed().when(['reminderStatus', 'status'], {
    is: (reminderStatus: ReminderStatusEnum, status: SelectionStatusEnum) =>
      reminderStatus === ReminderStatusEnum.SCHEDULE_PHASE &&
      status === SelectionStatusEnum.PENDING,
    then: yup
      .number()
      .nullable()
      .required('Schedule Item is a required field')
      .typeError('Schedule Item must be a number'),
    otherwise: yup.mixed().test('skip-validation', 'Skipping validation', () => true),
  }),
});

export const selectionSchemas: [typeof selectionGeneralSchema, typeof selectionReminderSchema] = [
  selectionGeneralSchema,
  selectionReminderSchema,
];

export const importSelectionsSchema = yup.object({
  items: yup.array().of(selectionGeneralSchema),
});
